import { CompanyId } from '@business/entities/companies';
import {
  EncryptionRequest,
  EncryptionResponse,
  GHLAccountResponse,
  GHLItemResponse,
  IntegrationId,
  IntegrationResponse,
  IntegrationType,
  IntuitUpdateSettings,
  QBUpdateSettings,
  CustomFieldResponse,
  ClickupUpdateSettings,
  RaveCustomFieldResponse,
  EmailBisonSyncStatus,
  EmailBisonCampaign,
} from '@business/entities/integration';
import { QBAccountItem, QBPaymentMethodItem } from '@business/entities/organization';
import { SalesTaxLineItems } from '@modules/common/InvoiceSalesTax';
import { Endpoint } from '../endpoint';
import { QBCustomField, QBImportRequest, QBModulesResponse } from './../../entities/organization/PaymentResponse';

export function createIntegration(data: Partial<IntegrationResponse>) {
  return Endpoint.post<IntegrationResponse>(`@gc/integrations`, { data });
}

export function updateQBSettings(data: QBUpdateSettings) {
  return Endpoint.post<IntegrationResponse>(`@gc/integrations/qb`, { data });
}

export function updateClickupSettings(data: ClickupUpdateSettings) {
  return Endpoint.post<IntegrationResponse>(`@gc/integrations/clickup`, { data });
}

export function updateIntuitSettings(data: IntuitUpdateSettings) {
  return Endpoint.post<IntegrationResponse>(`@gc/integrations/intuit`, { data });
}

export function importQB(data: QBImportRequest) {
  return Endpoint.post<void>(`@gc/integrations/qb-import`, { data });
}

export function importStripe(data: QBImportRequest) {
  return Endpoint.post<void>(`@gc/integrations/stripe-import`, { data });
}

export function importAuthorize(data: QBImportRequest) {
  return Endpoint.post<void>(`@gc/integrations/authorize-import`, { data });
}

export function deleteIntegration(id: IntegrationId) {
  return Endpoint.delete<void>(`@gc/integrations/${id}`);
}

export function getQBPaymentMethods() {
  return Endpoint.get<QBPaymentMethodItem[]>(`@gc/integrations/qb-payment-methods`);
}
export function getQBAccounts(companyId: CompanyId) {
  return Endpoint.get<QBAccountItem[]>(`@gc/integrations/qb-accounts/${companyId}`);
}

export function getQBCustomFields(companyId: CompanyId) {
  return Endpoint.get<QBCustomField[]>(`@gc/integrations/qb-custom-fields/${companyId}`);
}

export function getQBModules(companyId: CompanyId) {
  return Endpoint.get<QBModulesResponse>(`@gc/integrations/qb-modules/${companyId}`);
}

export function getStripeModules(companyId: CompanyId) {
  return Endpoint.get<QBModulesResponse>(`@gc/integrations/stripe-modules/${companyId}`);
}

export function getAuthorizeModules(companyId: CompanyId) {
  return Endpoint.get<QBModulesResponse>(`@gc/integrations/authorize-modules/${companyId}`);
}

export function getGHLForms(apiKey: string) {
  return Endpoint.post<GHLItemResponse>(`@gc/integrations/ghl-forms`, { data: { apiKey } });
}

export function getGHLAccounts() {
  return Endpoint.get<GHLAccountResponse[]>(`@gc/integrations/ghl-accounts`);
}
export function getEncryptedKey(data: EncryptionRequest) {
  return Endpoint.get<EncryptionResponse>(`@gc/integrations/encrypted-key`, { params: data });
}

export function saveConnection(data: string) {
  return Endpoint.post<EncryptionResponse>(`@gc/integrations/rave-connection`, { data: { key: data } });
}
export function calculateTax(type: IntegrationType, lines: SalesTaxLineItems[], addressId: string) {
  return Endpoint.post<number>(`@gc/integrations/calculate-tax/${type}`, { data: { lines, addressId } });
}

export function syncGemsOne() {
  return Endpoint.post<void>(`@gc/integrations/sync-gems-one`, { data: {} });
}

export function getSurveyMonkeyAuthUrl(companyId: CompanyId) {
  return Endpoint.post<{ url: string }>(`@gc/integrations/survey-monkey-endpoint`, { data: { companyId } });
}

export function getContactCustomFields() {
  return Endpoint.get<CustomFieldResponse[]>(`@gc/integrations/ac-custom-fields`);
}

export function getRaveCustomFields(comapnyId: string) {
  return Endpoint.get<RaveCustomFieldResponse[]>(`@gc/integrations/rave-custom-fields/${comapnyId}`);
}

export function testPaybiltConnection(bearerToken: string, environment: string) {
  return Endpoint.post<{ success: boolean; message: string }>('@gc/integrations/paybilt-test', {
    data: { bearerToken, environment },
  });
}

export function generatePaybiltRsaKey(bearerToken: string, environment: string) {
  return Endpoint.post<{ success: boolean; key_id: number; public_key: string }>('@gc/integrations/paybilt-rsa-key', {
    data: { bearerToken, environment },
  });
}

export function generatePaybiltLink(invoiceId: string) {
  return Endpoint.post<{ success: boolean; url: string }>('@gc/integrations/paybilt-link', {
    data: { invoiceId },
  });
}

/**
 * Email Bison Routes
 */
export function testEmailBisonConnection(apiKey: string, customServerUrl?: string) {
  return Endpoint.post<void>('@gc/emailbison/test-connection', {
    data: { apiKey, serverUrl: customServerUrl },
  });
}

export function syncEmailBisonContacts() {
  return Endpoint.post<void>('@gc/emailbison/sync-contacts');
}

export function getEmailBisonSyncStatus() {
  return Endpoint.get<EmailBisonSyncStatus>('@gc/emailbison/sync-status');
}

export function getEmailBisonCampaigns() {
  return Endpoint.get<EmailBisonCampaign[]>('@gc/emailbison/campaigns');
}

export function registerEmailBisonWebhook() {
  return Endpoint.post<{ success: boolean; message: string; webhookId?: string; webhookUrl?: string }>('@gc/emailbison/register-webhook');
}

export function deleteEmailBisonWebhook(webhookId: string) {
  return Endpoint.delete<{ success: boolean; message: string }>(`@gc/emailbison/webhook/${webhookId}`);
}

export function getEmailBisonWebhooks() {
  return Endpoint.get<any[]>('@gc/emailbison/webhooks');
}

